// Whether this practice can run a campaign at all.
//
// Shown before anyone builds one, because the alternative is what used to
// happen: upload a list, configure the calls, press launch, and only then be
// told "Agent 1234 has no outbound calling configuration" — true, and
// impossible to act on. It names neither the agent that would have worked nor
// the fact that the practice has no voice number.
//
// Switching outbound calling on for an agent is deliberately not something
// this app can do — the API will not write that flag — so this says who to ask
// rather than offering a button that cannot work.
import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Chip, Collapse, Link, Typography } from '@mui/material';
import { campaignApi, OutboundReadiness as Readiness } from '../services/campaignApi';

interface Props {
  /** Shown even when everything is fine. Off by default: a green banner on
   *  every visit is noise, and noise is what gets scrolled past. */
  alwaysShow?: boolean;
  onLoaded?: (ready: Readiness) => void;
}

const OutboundReadiness: React.FC<Props> = ({ alwaysShow, onLoaded }) => {
  const [state, setState] = useState<Readiness | null>(null);

  useEffect(() => {
    let live = true;
    campaignApi
      .getOutboundReadiness()
      .then((r) => { if (live) { setState(r); onLoaded?.(r); } })
      // Deliberately silent: this is advice, and a failed advisory read must
      // not stand between the practice and a campaign they can in fact run.
      .catch(() => {})
      ;
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state) return null;
  if (state.ready && !alwaysShow) return null;

  if (state.ready) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        {state.summary}
      </Alert>
    );
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>This account cannot place campaign calls yet</AlertTitle>
      {state.missing.map((m, i) => (
        <Typography key={i} variant="body2" sx={{ mb: 0.4 }}>{m}</Typography>
      ))}

      <Collapse in={state.agents_not_ready.length > 0}>
        <Box sx={{ mt: 1.2 }}>
          <Typography variant="caption" color="text.secondary">
            Agents on this account:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 0.5 }}>
            {state.agents_not_ready.map((a) => (
              <Chip
                key={a.agent_id}
                size="small"
                variant="outlined"
                label={`${a.name || `Agent ${a.agent_id}`} — ${a.why_not}`}
              />
            ))}
          </Box>
        </Box>
      </Collapse>

      <Typography variant="body2" sx={{ mt: 1.2 }}>
        Ask Yobi support to switch outbound calling on, or{' '}
        <Link href="mailto:support@yobi.app">email support</Link>.
      </Typography>
    </Alert>
  );
};

export default OutboundReadiness;
